import { useTranslation } from 'react-i18next'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../constants/common'
import { SearchEmpty } from '../SearchEmpty'
import { SearchListHeader } from '../SearchListHeader'
import { SearchListItems } from '../SearchListItems'
import { SearchLoader } from '../SearchLoader'
import { SearchSection } from '../SearchSection'

import * as styles from './SearchList.module.css'

/**
 * @import {SearchEmptyProps} from "../SearchEmpty/SearchEmpty"
 * @import {SearchListItemsProps} from "../SearchListItems/SearchListItems"
 * @import {SearchLoaderProps} from "../SearchLoader/SearchLoader"
 * @import {SearchSectionProps} from "../SearchSection/SearchSection"
 */

/**
 * @typedef {SearchSectionProps} SearchListProps
 *
 * @property {React.ComponentType<SearchEmptyProps>} [Empty]
 *
 * @property {React.ComponentType<SearchLoaderProps>} [Loader]
 *
 * @property {React.ComponentType<SearchListItemsProps>} [Items]
 *
 * @property {SearchEmptyProps} [emptyProps]
 *
 * @property {SearchEmptyProps} [itemsProps]
 *
 * @property {SearchLoaderProps} [loaderProps]
 *
 * @property {SearchListValue} value
 *
 * @property {(value: SearchItemValue) => void} [onSelect]
 *
 * @property {(search: SearchValue) => void} [onSearch]
 */

/**
 * @param {SearchListProps} props
 */
function SearchList({
  Empty = SearchEmpty,
  Header = SearchListHeader,
  Items = SearchListItems,
  Loader = SearchLoader,
  className = styles.SearchList,
  emptyProps: {
    className: emptyClassName = styles.empty,
    ...emptyProps
  } = EMPTY_OBJECT,
  headerProps: {
    className: headerClassName = styles.header,
    ...headerProps
  } = EMPTY_OBJECT,
  itemsProps: {
    className: itemsClassName = styles.items,
    ...itemsProps
  } = EMPTY_OBJECT,
  loaderProps: {
    className: loaderClassName = styles.loader,
    ...loaderProps
  } = EMPTY_OBJECT,
  title = 'Search.ResultsTitleKey',
  value,
  onSearch = EMPTY_FUNCTION,
  onSelect = EMPTY_FUNCTION,
  ...sectionProps
}) {
  const { items } = value

  const { t } = useTranslation()

  return (
    <SearchSection
      {...sectionProps}
      Header={Header}
      className={className}
      headerProps={{
        ...headerProps,
        className: headerClassName,
        value,
        onSearch,
      }}
      title={t(title)}
    >
      {items == null ? (
        <Loader {...loaderProps} className={loaderClassName} />
      ) : items.length === 0 ? (
        <Empty {...emptyProps} className={emptyClassName} />
      ) : (
        <Items
          {...itemsProps}
          className={itemsClassName}
          value={value}
          onSelect={onSelect}
        />
      )}
    </SearchSection>
  )
}

export default SearchList
